/**
 * `runStore` — le **miroir** de la simulation (SPEC-UI §1.5).
 *
 * Interdits repris tels quels de la spec : ne jamais muter l'état, ne jamais dériver une
 * règle de jeu ici, ne jamais faire d'optimistic update sur une jauge. Une action part, un
 * état neuf revient, il **remplace** l'ancien en bloc.
 *
 * Le store ne connaît pas la façon dont l'état est calculé : il parle au client de jeu **actif**
 * (`game/client.ts`), bac à sable local ou API serveur selon le choix du joueur. Il ne connaît
 * ni l'un ni l'autre par son nom — c'est ce qui a permis de brancher le serveur sans toucher un
 * seul écran.
 */

import { create } from "zustand";

import { messageErreur } from "../game/erreurs";
import type { EtageId } from "../game/escalier";
import type {
  ContexteAction,
  CreationRun,
  DispatchResult,
  Progression,
  RetourAuJalon,
  RunSnapshot,
} from "../game/gameClient";
import { clientDeJeu } from "../game/client";
import type { Action, ActionType, RecapDelegue, Recapitulatif } from "@maxime/shared";

import { useUIStore } from "./uiStore";

/**
 * ⟦A-16⟧ Ce que le panneau de récapitulatif a besoin de savoir : la décomposition rendue par le
 * moteur, et **quelle action** l'a produite.
 *
 * Le type d'action n'est pas un luxe : la ligne `cout_action` porte aussi bien un débit
 * (produire) qu'un crédit (dormir rend 80 ⚡ et 10 ❤), et l'écrire « le travail » dans les deux
 * cas serait faux. C'est de la mise en mots, pas une règle — le moteur reste la seule source
 * des chiffres.
 */
export interface RecapEnAttente {
  recap: Recapitulatif;
  action: ActionType;
}

/**
 * ⟦SPEC-UI §7.6⟧ **L'action qui vient de réussir**, pour ce qui s'entend à la racine de l'app
 * (`audio/SonsDuJeu.tsx` : `cash_in` / `cash_out` / `sleep_transition`).
 *
 * Ce n'est PAS une règle de jeu : le type de l'action et deux lectures du solde, rien que le
 * store connaissait déjà. `id` monte à chaque action pour que deux achats d'affilée se
 * distinguent — un `useEffect` sur un objet identique ne se rejouerait pas.
 */
export interface DerniereAction {
  id: number;
  type: ActionType;
  argent_avant: number;
  argent_apres: number;
}

/**
 * ⟦SPEC-UI §7.5 · A-83⟧ **La crise de SM = 0 que le tour vient de déclencher** — ce que l'écran
 * « Crise — 14 jours d'arrêt forcé » raconte. Lu tel quel dans l'événement `crisis_started` du
 * moteur (`abonnes_perdus`, `jours`, `rente_deduite`) et dans le `warning` qui l'accompagne ;
 * rien n'est re-dérivé de l'historique. `terminal` : la crise a été la dernière (§11.2) — le
 * tour porte un game over ou un retour au jalon.
 */
export interface CriseEnAttente {
  abonnes_perdus: number;
  jours: number;
  rente_deduite: number;
  dernier_avertissement: boolean;
  terminal: boolean;
}

/** Ce que `crisis_started` dit, ou `null` si ce tour n'en porte pas. */
export function criseDuTour(
  events: readonly { kind: string; data: Record<string, unknown> }[],
  terminal: boolean,
): CriseEnAttente | null {
  const debut = events.find((e) => e.kind === "crisis_started");
  if (!debut) return null;
  const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
  return {
    abonnes_perdus: n(debut.data.abonnes_perdus),
    jours: n(debut.data.jours),
    rente_deduite: n(debut.data.rente_deduite),
    dernier_avertissement: events.some(
      (e) => e.kind === "warning" && e.data.cause === "dernier_avertissement_burnout",
    ),
    terminal,
  };
}

interface RunStore {
  run: RunSnapshot | null;
  /** Une action est en vol : les boutons se désactivent (SPEC-UI §2, pas de double-clic). */
  pending: boolean;
  /** L'action qui vient de réussir — `null` au boot et après un refus. */
  derniereAction: DerniereAction | null;
  /** ⟦SPEC-UI §7.5⟧ La crise à raconter, plein écran, avant tout le reste. */
  crise: CriseEnAttente | null;
  /** Le chargement initial a-t-il eu lieu ? Évite de flasher « pas de run » au boot. */
  pret: boolean;
  /** ⟦A-5⟧ File des ouvertures à annoncer — vidée par le composant `MomentsDebloques`. */
  deverrouillagesEnAttente: EtageId[];
  /** ⟦A-4 #13⟧ Paliers franchis à célébrer — vidés par le composant `PopupsPalier`. */
  paliersEnAttente: number[];
  /**
   * La chute à raconter (game over en mode normal, SPEC-API §7.3). Elle passe **devant** tout
   * le reste : on ne fête pas un palier juste après s'être écroulé.
   */
  chute: RetourAuJalon | null;
  /**
   * Le million, tel que le tour vient de le graver — `ending` et **score sur 1 000**.
   *
   * ⚠️ Ce n'est **pas** ce qui aiguille vers l'écran de victoire : celui-ci est routé sur
   * l'ÉTAT (`status === "won"`), pour qu'un F5 juste après le million ne fasse pas disparaître
   * le choix « clore ou continuer ». Ce champ ne porte que ce que l'état ne sait pas dire : le
   * score, qui est calculé par la couche serveur (§7.4) et vaut donc `null` en bac à sable.
   */
  victoire: Progression["victoire"];
  /**
   * ⟦A-16⟧ Le récapitulatif à montrer, ou `null`. Posé dès qu'une action a franchi **au moins
   * une frontière de jour** — c'est le seul endroit où le joueur apprend que le temps coûte.
   * Vidé par `RecapJours` quand il ferme le panneau.
   */
  recapEnAttente: RecapEnAttente | null;
  /**
   * ⟦SPEC-PRODUCTEUR §5.5 / A-66⟧ Le récapitulatif de la dernière délégation, ou `null`.
   *
   * Il **survit aux actions suivantes** — contrairement à `recapEnAttente`, qui appartient à
   * l'action qui vient de finir. Motif : le joueur relit ce qui s'est passé pendant qu'il ne
   * regardait pas, puis va trancher son événement, puis revient sur l'écran de l'équipe.
   * L'effacer au premier tour d'après lui retirerait le seul compte-rendu qu'il ait.
   */
  delegationEnAttente: RecapDelegue | null;
  charger(): Promise<void>;
  creer(input: CreationRun): Promise<RunSnapshot>;
  /**
   * Toute action de jeu passe par ici. Rend `null` si le moteur a refusé (toast affiché).
   *
   * `contexte` porte ce qui accompagne l'action sans en faire partie — aujourd'hui, les
   * timelines d'Atelier que le serveur rescorera (SPEC-API §6.4). Le bac à sable les ignore.
   */
  act(action: Action, contexte?: ContexteAction): Promise<DispatchResult | null>;
  consommerDeverrouillage(): void;
  consommerPalier(): void;
  consommerChute(): void;
  consommerCrise(): void;
  consommerRecap(): void;
  abandonner(): Promise<void>;
}

let prochaineAction = 1;

export const useRunStore = create<RunStore>((set, get) => ({
  run: null,
  pending: false,
  derniereAction: null,
  crise: null,
  pret: false,
  deverrouillagesEnAttente: [],
  paliersEnAttente: [],
  chute: null,
  victoire: null,
  recapEnAttente: null,
  delegationEnAttente: null,

  async charger() {
    try {
      const run = await clientDeJeu().charger();
      set({ run, pret: true });
    } catch (err) {
      // ⚠️ **`pret` passe à `true` même en échec, et c'est le point important.** Depuis que le
      // chargement traverse le réseau, il peut rater (serveur injoignable, session expirée
      // pendant la requête). Sans ce `catch`, la promesse partait en rejet non capturé et
      // l'écran restait sur « Ouverture du studio… » — indéfiniment, sans un mot. On dit ce qui
      // s'est passé, et on rend la main : le joueur peut au moins repartir. Le bac à sable, lui,
      // n'échoue pas ici.
      set({ run: null, pret: true });
      useUIStore.getState().toast({ texte: messageErreur(err), ton: "mal", icone: "📡" });
    }
  },

  async creer(input) {
    const run = await clientDeJeu().creerRun(input);
    set({
      run,
      deverrouillagesEnAttente: [],
      paliersEnAttente: [],
      chute: null,
      crise: null,
      victoire: null,
      recapEnAttente: null,
      pret: true,
    });
    return run;
  },

  async act(action, contexte) {
    if (get().pending) return null;

    // Un `pending_event` BLOQUE toute autre action (SPEC-MOTEUR §2.3, arbitrage A24) — le
    // moteur le refuse déjà, mais on ne l'envoie même pas : c'est un aller-retour réseau pour
    // rien une fois le serveur branché, et surtout la règle est ici **écrite une fois**.
    //
    // Le piège que ça ferme, signalé par la session serveur après l'avoir payé en tests
    // instables : dormir tire un événement de nuit (§2.4 pas 7), donc **enchaîner deux
    // actions sans regarder `pending_event` échoue une fois sur deux, selon la graine**.
    // Aujourd'hui chaque action vient d'un clic et la modale couvre l'écran ; demain un
    // `serverGameClient` ou un test pourrait enchaîner — et casser par intermittence.
    const enAttente = get().run?.state.pending_event;
    if (enAttente && action.type !== "choix_evenement") {
      useUIStore.getState().toast({
        texte: "Règle d'abord l'événement en cours : le reste attend.",
        ton: "mal",
        icone: "⏸",
      });
      return null;
    }

    const argentAvant = get().run?.state.money ?? 0;
    set({ pending: true });
    try {
      const resultat = await clientDeJeu().dispatch(action, contexte);
      // Les paliers et la chute viennent du bloc `progression` (SPEC-API §5.5 + M2.7) :
      // l'aiguillage est explicite, on ne le devine plus depuis `state.status`.
      set({
        run: resultat.run,
        // ⟦SPEC-UI §7.6⟧ Ce qui s'entend se lit ici, une fois par action réussie.
        derniereAction: {
          id: prochaineAction++,
          type: action.type,
          argent_avant: argentAvant,
          argent_apres: resultat.run.state.money,
        },
        deverrouillagesEnAttente: [
          ...get().deverrouillagesEnAttente,
          ...resultat.deverrouillages,
        ],
        paliersEnAttente: [
          ...get().paliersEnAttente,
          ...resultat.progression.paliers_franchis,
        ],
        chute: resultat.progression.retour_au_jalon ?? get().chute,
        // ⟦SPEC-UI §7.5⟧ La crise passe DEVANT la chute et la fin : on raconte d'abord ce qui
        // vient d'arriver, la suite (retour au jalon, game over) prend le relais quand on
        // continue. Un tour sans crise n'écrase pas une crise pas encore lue.
        crise:
          criseDuTour(
            resultat.events,
            resultat.progression.retour_au_jalon !== null ||
              resultat.progression.fin_verrouillee !== null ||
              resultat.run.state.status === "game_over",
          ) ?? get().crise,
        // Une seule fois dans la vie d'une run — on ne l'écrase pas avec le `null` des tours
        // suivants, sans quoi le score disparaîtrait dès la première action d'après-million.
        victoire: resultat.progression.victoire ?? get().victoire,
        // ⟦A-16⟧ Aucune frontière franchie ⇒ rien à raconter, et surtout **pas de panneau** :
        // deux shorts sur trois tiennent dans la journée, ils n'ouvrent rien. On efface aussi
        // le précédent dans ce cas — un récapitulatif appartient à l'action qui vient de finir.
        recapEnAttente:
          resultat.recap && resultat.recap.jours_traverses > 0
            ? { recap: resultat.recap, action: action.type }
            : null,
        // ⟦A-66⟧ On ne l'écrase PAS avec le `null` des tours suivants (voir le champ).
        delegationEnAttente: resultat.delegation ?? get().delegationEnAttente,
      });
      // Les faits marquants de l'action passent en toasts (SPEC-UI §2 : les deltas se voient).
      const { toast } = useUIStore.getState();
      for (const entree of resultat.nouvellesEntrees) {
        if (entree.ton === "info" || entree.ton === "deverrouillage") continue;
        toast({ texte: entree.texte, ton: entree.ton, icone: entree.icone });
      }
      return resultat;
    } catch (err) {
      useUIStore.getState().toast({ texte: messageErreur(err), ton: "mal", icone: "🚫" });
      // Un refus peut avoir **remis le miroir à jour** : c'est le cas de la désynchronisation
      // (§7.2 — un autre onglet a joué, le client a refait `GET /runs/active` avant de rendre
      // la main). `getState()` est le miroir synchrone du contrat : on le relit plutôt que de
      // laisser à l'écran un état dont on sait qu'il est périmé. En bac à sable il n'a pas
      // bougé, et cette ligne ne fait rien — c'est ce qui la rend sûre pour les deux.
      const miroir = clientDeJeu().getState();
      if (miroir && miroir !== get().run) set({ run: miroir });
      return null;
    } finally {
      set({ pending: false });
    }
  },

  consommerDeverrouillage() {
    set({ deverrouillagesEnAttente: get().deverrouillagesEnAttente.slice(1) });
  },

  consommerPalier() {
    set({ paliersEnAttente: get().paliersEnAttente.slice(1) });
  },

  consommerChute() {
    set({ chute: null });
  },

  consommerCrise() {
    set({ crise: null });
  },

  consommerRecap() {
    set({ recapEnAttente: null });
  },

  async abandonner() {
    await clientDeJeu().abandonner();
    set({
      run: null,
      deverrouillagesEnAttente: [],
      paliersEnAttente: [],
      chute: null,
      crise: null,
      victoire: null,
      recapEnAttente: null,
    });
  },
}));

/** Raccourci de lecture : l'état de jeu, ou `null`. Les écrans gardés savent qu'il existe. */
export function useGameState() {
  return useRunStore((s) => s.run?.state ?? null);
}
